import { FC, useEffect } from "react";
import {
  getAllocationStatus,
  getShareSummaryTableData,
  IssuedByModel,
  ShareIssueModel,
  ShareTypeIssueModel,
  SubscriberModel,
  SummaryTableRow,
} from "./issueTransactionDomain";
import { observer } from "mobx-react";
import { TMessage } from "../../../i18n/tt";
import { DataTable, TableColumn } from "../../../components-v2/DataTable";
import { compact } from "lodash";
import { Checkbox } from "../../../components-v2/Checkbox";
import { Box, VBox } from "../../../components-v2/Box";
import { IssuedBy } from "../../../types/const/Transactions";
import {
  ShareBonusIssueModel,
  SubscriberForBonusIssueModel,
} from "../../../dbot/capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueDomain";
import { MTextInput } from "../../../components-v2/TextInput";
import { Button } from "../../../components-v2/Button";
import { isMobileView } from "../../../utils/screen";
import { MSelect } from "../../../components-v2/Select";
import { MDateInput } from "../../../components-v2/DateInput";
import { Alert } from "../../../components-v2/Alert";
import { useLanguage, useLocaleFormat, useTranslation } from "../../../stores/RootStoreContext";
import { formatAmountNumber } from "../../../utils/currency";
import { BooleanFieldModel, NumberFieldModel } from "../../../common/commonForms";
import { RadioFieldQuestion } from "../../../common/commonComponents";
import { ShareType, shareTypeLabel, shareTypeName } from "../../../common/share";

export const ShareClassName: FC<{ shareType: ShareType }> = observer(({ shareType }) => {
  const { t } = useTranslation();

  return <div>{t(shareTypeLabel(shareType))}</div>;
});

export const MoneyInput: FC<{
  name: string;
  label: string;
  amount: NumberFieldModel;
  currency: string;
}> = observer(({ name, label, amount, currency }) => {
  const locale = useLanguage();

  useEffect(() => {
    amount.setLocale(locale);
  }, [locale, amount]);

  return <MTextInput name={name} label={label} prefix={currency} model={amount} />;
});

const ShareTypeIssueRow: FC<{
  model: ShareTypeIssueModel;
  isBonusIssue?: boolean;
}> = observer(({ model, isBonusIssue }) => {
  const { t } = useTranslation();
  return (
    <VBox data-testid={`shareClass-${model.shareType.name}`}>
      <h5>
        <ShareClassName shareType={model.shareType} />
      </h5>
      <Box vertical={isMobileView()}>
        <MTextInput
          name={`shareClasses.${model.shareType.name}.number`}
          label={t("transactions.issueTransaction.number-of-new-shares")}
          model={model.numberOfShares}
          onChange={(e) => {
            model.numberOfShares.setInputValue(e);
            model.isPriceRequired(isBonusIssue);
          }}
        />
        {!isBonusIssue && (
          <MTextInput
            name={`shareClasses.${model.shareType.name}.price`}
            label={t("transactions.issueTransaction.share-price")}
            model={model.price}
            prefix={model.currency}
          />
        )}
      </Box>
    </VBox>
  );
});

export const ShareIssueList: FC<{
  model: ShareIssueModel;
  isBonusIssue?: BooleanFieldModel;
  onAddShareClass: () => void;
}> = observer(({ model, isBonusIssue, onAddShareClass }) => {
  const { t } = useTranslation();
  return (
    <>
      {model.shareTypeIssues.map((shareTypeIssue) => (
        <ShareTypeIssueRow
          key={shareTypeIssue.shareType.sharetypeId}
          model={shareTypeIssue}
          isBonusIssue={isBonusIssue?.value}
        />
      ))}
      <div>
        <Button
          icon={"plus"}
          label={t("add.shareclass")}
          variant={"secondary"}
          onClick={onAddShareClass}
        />
      </div>
    </>
  );
});

const CurrencySelector: FC<{ subscriber: SubscriberModel }> = observer(({ subscriber }) => {
  return <MSelect model={subscriber.currency} />;
});

export const ShareSummary: FC<{
  model: ShareIssueModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();
  const locale = useLanguage();
  const tableColumns: Array<TableColumn<SummaryTableRow>> = [
    {
      name: "name",
      label: "",
      align: "left",
      getValue: (s) => t(s.name),
    },
    {
      name: "sharesBefore",
      label: t("transactions.issueTransaction.shares-before"),
      notSorted: true,
      getValue: (s) => formatNumber(s.sharesBefore),
    },
    {
      name: "sharesAfter",
      label: t("transactions.issueTransaction.shares-after"),
      notSorted: true,
      getValue: (s) => formatNumber(s.sharesAfter),
    },
    {
      name: "price",
      label: t("transactions.issueTransaction.nominal-amount"),
      notSorted: true,
      getValue: (s) => formatNumber(s.price),
    },
    {
      name: "shareCapitalBefore",
      label: t("transactions.issueTransaction.shareCapitalBefore"),
      notSorted: true,
      getValue: (s) => formatAmountNumber(s.shareCapitalBefore, locale),
    },
    {
      name: "newShareCapital",
      label: t("transactions.issueTransaction.new-share-capital"),
      notSorted: true,
      getValue: (s) => formatAmountNumber(s.newShareCapital, locale),
    },
  ];
  return (
    <DataTable
      variant="IN_CARD_DENSE"
      tableColumns={tableColumns}
      tableData={getShareSummaryTableData(model)}
      rowProps={(row) => ({
        className: row.noChange ? "subtle" : undefined,
      })}
      getId={(row) => row.id}
    />
  );
});

export const AllocationStatus: FC<{
  model: ShareIssueModel | ShareBonusIssueModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();
  const allocationStatuses = getAllocationStatus(model.shareTypeIssues, model.subscribers);
  if (!allocationStatuses.length) {
    return null;
  }
  return (
    <Box className="tw-sticky tw-top-0 tw-z-[1]" bg={"alt2"} textSize={"xs"} padding={"sm"}>
      <strong>{t("transactions.issueTransaction.remaining")}</strong>
      {allocationStatuses.map(({ shareType, total, remaining }) => (
        <div key={shareType.sharetypeId}>
          <strong>{t(shareTypeLabel(shareType))}</strong>
          <div>
            {formatNumber(remaining)} / {formatNumber(total)}
          </div>
        </div>
      ))}
    </Box>
  );
});

export const Subscribers: FC<{
  model: ShareIssueModel | ShareBonusIssueModel;
  isBonusIssue?: BooleanFieldModel;
}> = observer(({ model, isBonusIssue }) => {
  const { t } = useTranslation();
  const allocationErrorMessages =
    model instanceof ShareIssueModel ? model.allocationErrorMessages : [];
  const shareTypes = model.activeShareTypeIds;
  const isDeviatingPrice =
    model instanceof ShareIssueModel && model.isDeviatingPrice.value && !isBonusIssue?.value;
  const columns: TableColumn<SubscriberModel | SubscriberForBonusIssueModel>[] = compact([
    {
      name: "shareholder.name",
      label: t("transactions.issueTransaction.subscriber"),
      align: "left",
      getValue: (subscriber) => (
        <div className={"whitespace-nowrap"}>
          <span>{subscriber.shareholder.name}</span>
          {subscriber.shareholder.birthDateOrOrganisationNumber && (
            <span className={"subtle"}>
              {" "}
              {subscriber.shareholder.birthDateOrOrganisationNumber}
            </span>
          )}
        </div>
      ),
    },
    {
      name: "shareType",
      label: t("transactions.issueTransaction.share-class"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) => <div>{t(shareTypeName(subscriber.shareType))}</div>,
    },
    {
      name: "numberOfShares",
      label: t("transactions.issueTransaction.number-of-shares"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) => (
        <MTextInput
          name={`subscriber.${subscriber.shareholder.name}.${subscriber.shareType.name}.number`}
          model={subscriber.numberOfShares}
        />
      ),
    },
    isDeviatingPrice && {
      name: "price",
      label: t("transactions.issueTransaction.price-per-share"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) =>
        subscriber instanceof SubscriberModel ? (
          <MTextInput
            name={`subscriber.${subscriber.shareholder.name}.${subscriber.shareType.name}.price`}
            model={subscriber.price}
          />
        ) : null,
    },
    isDeviatingPrice && {
      name: "currency",
      label: t("shareholders.currency"),
      align: "left",
      notSorted: true,
      getValue: (subscriber) =>
        subscriber instanceof SubscriberModel ? (
          <CurrencySelector subscriber={subscriber as SubscriberModel} />
        ) : null,
    },
  ]);
  if (!shareTypes.length) {
    return null;
  }

  return (
    <>
      {allocationErrorMessages.map((error) => (
        <Alert key={t(error)} message={t(error)} type={"error"} />
      ))}
      <DataTable
        variant={"EDITABLE_DENSE"}
        tableColumns={columns}
        tableData={model.subscribers}
        rowFilter={(s) => shareTypes.includes(s.shareType.sharetypeId)}
        context={{
          shouldSkipCell: (row, col) => col === 0 && row % shareTypes.length > 0,
          getRowSpan: (row, col) => {
            if (col === 0 && row % shareTypes.length === 0) {
              return shareTypes.length;
            }
          },
        }}
        getId={(row) => row.id}
      />
    </>
  );
});

export const DeviatingPriceCheckbox: FC<{
  model: BooleanFieldModel;
  isBonusIssue?: BooleanFieldModel;
}> = observer(({ model, isBonusIssue }) => {
  const { t } = useTranslation();
  if (isBonusIssue?.value) {
    return null;
  }
  return (
    <Checkbox
      label={t("transactions.issueTransaction.deviatingPrice")}
      checked={model.value ?? false}
      onChange={(event) => model.setValue(event.target.checked)}
    />
  );
});

export const IssuedByView: FC<{ model: IssuedByModel }> = observer(({ model }) => {
  const { t } = useTranslation();
  const dateLabel: TMessage =
    model.issuedBy.value === IssuedBy.BOARD_MEETING
      ? { key: "transactions.issueTransaction.boardMeetingPoADate" }
      : { key: "transactions.issueTransaction.generalMeetingDate" };

  return (
    <>
      <RadioFieldQuestion
        field={model.issuedBy}
        fieldName={"issuedBy"}
        question={{ key: "transactions.issueTransaction.issuedBy" }}
      />
      <MDateInput label={t(dateLabel)} model={model.decisionDate} inline={true} />
    </>
  );
});
